import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Star, Users, BookOpen, Award } from 'lucide-react';

const Hero = ({ company }) => {
  // Use company data with fallbacks
  const tagline = company?.tagline || "Elevate Your Engineering Skills with Industry Experts";
  const description = company?.description || "Join our comprehensive training programs designed by industry professionals to help you master the latest engineering technologies and advance your career.";

  // Stats with fallback values
  const stats = {
    studentsTrained: company?.stats?.studentsTrained ? company.stats.studentsTrained.toLocaleString() + '+' : "5,000+",
    coursesOffered: company?.stats?.coursesOffered ? company.stats.coursesOffered.toLocaleString() + '+' : "50+",
    successRate: company?.stats?.successRate ? company.stats.successRate + '%' : "98%",
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center mt-20 justify-center overflow-hidden bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-hero-pattern opacity-5"></div>
      
      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          variants={floatingVariants}
          animate="animate"
          className="absolute top-20 left-10 w-20 h-20 bg-primary-200 rounded-full opacity-20"
        />
        <motion.div
          variants={floatingVariants}
          animate="animate"
          className="absolute top-40 right-20 w-16 h-16 bg-accent-200 rounded-full opacity-20"
          style={{ animationDelay: '2s' }}
        />
        <motion.div
          variants={floatingVariants}
          animate="animate"
          className="absolute bottom-40 left-20 w-12 h-12 bg-primary-300 rounded-full opacity-20"
          style={{ animationDelay: '4s' }}
        />
        <motion.div
          variants={floatingVariants}
          animate="animate"
          className="absolute bottom-20 right-10 w-24 h-24 bg-accent-300 rounded-full opacity-20"
          style={{ animationDelay: '1s' }}
        />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center lg:text-left"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                <Star className="w-4 h-4 mr-2" />
                #1 Engineering Training Platform
              </span>
            </motion.div>

            <motion.h1 
              variants={itemVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight"
            >
              {tagline}
            </motion.h1>

            <motion.p 
              variants={itemVariants}
              className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed max-w-2xl"
            >
              {description}
            </motion.p>

            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Link
                to="/courses"
                className="group btn btn-primary btn-xl inline-flex items-center"
              >
                Explore Courses
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              {/* <button className="group btn btn-outline btn-xl inline-flex items-center">
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                Watch Demo
              </button> */}
            </motion.div>

            {/* Quick Stats */}
            <motion.div 
              variants={itemVariants}
              className="mt-12 grid grid-cols-3 gap-8"
            >
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {stats.studentsTrained}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Students Trained</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {stats.coursesOffered}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Courses Offered</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {stats.successRate}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Success Rate</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Hero Image/Video */}
          <motion.div
            variants={itemVariants}
            className="relative"
          >
            <div className="relative">
              {/* Main Hero Image */}
              <div className="relative w-full h-96 lg:h-[500px] bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900 dark:to-accent-900 rounded-2xl overflow-hidden shadow-2xl">
                {company?.heroImage ? (
                  <img 
                    src={company.heroImage} 
                    alt="Prime Edge Engineering Training" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-accent-600/20"></div>
                )}
                
                {/* Floating Cards */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, duration: 0.8 }}
                  className="absolute top-8 left-8 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">Live Classes</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Interactive Learning</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2, duration: 0.8 }}
                  className="absolute top-8 right-8 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-accent-100 dark:bg-accent-900 rounded-lg flex items-center justify-center">
                      <Award className="w-5 h-5 text-accent-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">Certification</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Industry Recognized</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.4, duration: 0.8 }}
                  className="absolute bottom-8 left-8 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">Expert Instructors</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Industry Professionals</div>
                    </div>
                  </div>
                </motion.div>

                {/* Play Button Overlay */}
                <motion.button
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.6, duration: 0.5 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="absolute inset-0 flex items-center justify-center group"
                >
                  <div className="w-20 h-20 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                    <Play className="w-8 h-8 text-primary-600 ml-1" />
                  </div>
                </motion.button>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-200 dark:bg-primary-800 rounded-full opacity-50"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-accent-200 dark:bg-accent-800 rounded-full opacity-50"></div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-gray-400 dark:border-gray-600 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-gray-400 dark:bg-gray-600 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;

